import { useState } from "react";
import { FileSpreadsheet, FileText, Globe, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { downloadBlob } from "@/lib/download-blob";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultScanId?: string;
  defaultOwnership?: string;
  defaultClassification?: string;
  collectionId?: string;
}

export function ExportDialog({
  open,
  onOpenChange,
  defaultScanId,
  defaultOwnership,
  defaultClassification,
  collectionId,
}: ExportDialogProps) {
  const [scanId, setScanId] = useState(defaultScanId ?? "all");
  const [ownership, setOwnership] = useState(defaultOwnership ?? "all");
  const [classification, setClassification] = useState(defaultClassification ?? "all");
  const [format, setFormat] = useState<"csv" | "xlsx" | "kml">("csv");
  const [selectedColumns, setSelectedColumns] = useState<Set<string> | null>(null);

  const { data: allColumns } = trpc.export.columns.useQuery(undefined, { enabled: open });
  const { data: scansData } = trpc.scans.list.useQuery({ limit: 100, offset: 0 }, { enabled: open && !collectionId });

  const csvMutation = trpc.export.csv.useMutation();
  const xlsxMutation = trpc.export.xlsx.useMutation();
  const kmlMutation = trpc.export.kml.useMutation();
  const isExporting = csvMutation.isPending || xlsxMutation.isPending || kmlMutation.isPending;

  // Initialize selected columns with defaults on first load
  const defaultKeys = new Set([
    "name", "primary_type", "address", "lat", "lng", "rating",
    "ownership", "has_sport_field", "confidence", "source",
  ]);
  const cols = selectedColumns ?? defaultKeys;

  function toggleColumn(key: string) {
    const next = new Set(cols);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedColumns(next);
  }

  async function handleExport() {
    const input = {
      scanId: scanId !== "all" ? scanId : undefined,
      collectionId,
      ownership: ownership !== "all" ? (ownership as "public" | "private" | "institutional" | "unknown") : undefined,
      classification: classification !== "all" ? (classification as "sport_field" | "no_field" | "unclassified") : undefined,
      columns: [...cols],
    };

    if (format === "csv") {
      const result = await csvMutation.mutateAsync(input);
      downloadBlob(result.data, result.filename, result.mimeType);
    } else if (format === "xlsx") {
      const result = await xlsxMutation.mutateAsync(input);
      downloadBlob(result.data, result.filename, result.mimeType, true);
    } else {
      const result = await kmlMutation.mutateAsync(input);
      downloadBlob(result.data, result.filename, result.mimeType);
    }
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export Locations</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Scan selector (hidden for collection exports) */}
          {!collectionId && (
            <div className="space-y-1.5">
              <Label>Scan</Label>
              <Select value={scanId} onValueChange={setScanId}>
                <SelectTrigger>
                  <SelectValue placeholder="All Scans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scans</SelectItem>
                  {scansData?.scans.map((scan) => (
                    <SelectItem key={scan.id} value={scan.id}>
                      {new Date(scan.created_at).toLocaleDateString()} ({scan.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filters row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ownership</Label>
              <Select value={ownership} onValueChange={setOwnership}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="institutional">Institutional</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Classification</Label>
              <Select value={classification} onValueChange={setClassification}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="sport_field">Sport Field</SelectItem>
                  <SelectItem value="no_field">No Field</SelectItem>
                  <SelectItem value="unclassified">Unclassified</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Column checkboxes (hidden for KML) */}
          {format !== "kml" && (
            <div className="space-y-1.5">
              <Label>Columns</Label>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 max-h-48 overflow-y-auto rounded border p-3">
                {(allColumns ?? []).map((col) => (
                  <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={cols.has(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                    />
                    {col.header}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Format */}
          <div className="space-y-1.5">
            <Label>Format</Label>
            <div className="flex gap-3">
              <Button
                variant={format === "csv" ? "default" : "outline"}
                size="sm"
                onClick={() => setFormat("csv")}
              >
                <FileText className="h-4 w-4 mr-1.5" />
                CSV
              </Button>
              <Button
                variant={format === "xlsx" ? "default" : "outline"}
                size="sm"
                onClick={() => setFormat("xlsx")}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                XLSX
              </Button>
              <Button
                variant={format === "kml" ? "default" : "outline"}
                size="sm"
                onClick={() => setFormat("kml")}
              >
                <Globe className="h-4 w-4 mr-1.5" />
                KML
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting || (format !== "kml" && cols.size === 0)}>
            {isExporting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Export {format.toUpperCase()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
